<template lang="pug">
.v-datepicker.datepicker(:class="{'open': show, 'datepicker--static': staticMode}")
	.datepicker
		input.datepicker__field-input.field-input(v-if="!staticMode"
				type="text"
				readonly
				:value="pickedValue"
				:disabled="field.disabled"
				@focus="show = true; $emit('focus')")
		.datepicker__container
			table.datepicker__calendar
				thead
					tr.datepicker__head
						th.datepicker__head-cell(colspan="7")
							.datepicker__head-row
								.datepicker__head-month
									span.datepicker__btn-prev(@click="monthClick(-1)")
									span.datepicker__month {{months[now.getMonth()]}}
									span.datepicker__btn-next(@click="monthClick(1)")
								.datepicker__head-year
									span.datepicker__btn-prev(@click="yearClick(-1)")
									span.datepicker__year {{now.getFullYear()}}
									span.datepicker__btn-next(@click="yearClick(1)")
					tr.datepicker__body
						th(v-for="day in days" :key="day") {{day}}
				tbody
					tr(v-for="(v, i) in 6" :key="i")
						td(v-for="(v, j) in 7" :key="j"
								:class="date[i * 7 + j] && date[i * 7 + j].status"
								@click="(date[i * 7 + j] && date[i * 7 + j].disabled) ? null : pickDate(i * 7 + j)")
							| {{date[i * 7 + j] && date[i * 7 + j].text}}
		v-svg-icon.datepicker__svg-icon(icon="calendar")
</template>

<script>
export default {
	props: {
		field: {
			type: Object,
			default: null
		},

		staticMode: {
			type: Boolean,
			default: false
		},

		rangeMode: {
			type: Boolean,
			default: false
		},

		modelValue: {
			type: [Number, Array],
			default: null
		}
	},

	emits: [
		'update:model-value',
		'focus',
		'blur',
		'opened',
		'closed'
	],

	data() {
		return {
			show: false,
			days: [
				'Пн',
				'Вт',
				'Ср',
				'Чт',
				'Пт',
				'Сб',
				'Вс'
			],
			months: [
				'Январь',
				'Февраль',
				'Март',
				'Апрель',
				'Май',
				'Июнь',
				'Июль',
				'Август',
				'Сентябрь',
				'Октябрь',
				'Ноябрь',
				'Декабрь'
			],
			date: [],
			now: new Date(),
			localValue: [],
			pickedValue: '',
			rangeSelectionActive: false
		};
	},

	computed: {
		minDateAsDate() {
			return this.field.minDate ? this.getLimitingDate(this.field.minDate) : null;
		},

		maxDateAsDate() {
			return this.field.maxDate ? this.getLimitingDate(this.field.maxDate) : null;
		}
	},

	watch: {
		now() {
			this.update();
		},

		show() {
			this.update();
			this.$emit(this.show ? 'opened' : 'closed');
		},

		field: {
			handler() {
				this.localValue = JSON.parse(JSON.stringify(this.field.value || [this.toUnixTime(new Date())] ));
				this.pickedValue = this.localValue ? this.stringify(this.fromUnixTime(this.localValue[0])) : null;
				this.rangeSelectionActive = false;
				this.update();
			},
			deep: true
		}
	},

	created() {
		this.localValue = JSON.parse(JSON.stringify(this.field.value || [this.toUnixTime(new Date())]));
		this.pickedValue = this.localValue ? this.stringify(this.fromUnixTime(this.localValue[0])) : null;
		this.$nextTick(() => {
			this.now = this.parse(this.pickedValue) || new Date();
			document.addEventListener('click', this.leave, false);
			document.addEventListener('focusin', this.focusLeave, false);
		});
	},

	beforeUnmount() {
		document.removeEventListener('click', this.leave, false);
		document.removeEventListener('focusin', this.focusLeave, false);
	},

	methods: {
		close() {
			this.show = false;
		},

		update() {
			let arr = [];
			let time = new Date(this.now);
			time.setMonth(time.getMonth(), 1); // the first day of a viewed month
			let curFirstDay = time.getDay() - 1; // - 1 used to start week from monday not sunday
			if (curFirstDay === -1) {
				curFirstDay = 6;
			}
			if (curFirstDay === 0) {
				curFirstDay = 7;
			}
			time.setDate(0); // 0 sets day to the last day of prev month
			let lastDayCount = time.getDate();
			for (let i = curFirstDay; i > 0; i--) {
				let tmpTime = new Date(time.getFullYear(), time.getMonth(), lastDayCount - i + 1);
				arr.push({
					text: lastDayCount - i + 1,
					time: tmpTime,
					status: 'date-pass ' + this.getStatus(tmpTime),
					disabled: !this.isDateWithinRange(tmpTime)
				});
			}
			time.setMonth(time.getMonth() + 2, 0); // the last day of this month
			let curDayCount = time.getDate();
			time.setDate(1); // fix bug when month change
			for (let i = 0; i < curDayCount; i++) {
				let tmpTime = new Date(time.getFullYear(), time.getMonth(), i + 1);
				arr.push({
					text: i + 1,
					time: tmpTime,
					status: this.getStatus(tmpTime),
					disabled: !this.isDateWithinRange(tmpTime)
				});
			}
			// fill calendar with next month days
			for (let j = 1; arr.length < 42; j++) {
				let tmpTime = new Date(time.getFullYear(), time.getMonth() + 1, j);
				arr.push({
					text: j,
					time: tmpTime,
					status: 'date-future ' + this.getStatus(tmpTime),
					disabled: !this.isDateWithinRange(tmpTime)
				});
			}
			this.date = arr;
		},

		isDateHighlighted(date) {
			if (this.field.highlightDates && date) {
				return this.field.highlightDates.indexOf(this.toUnixTime(date)) >= 0;
			}
			return false;
		},

		isDateWithinRange(date) {
			if (this.minDateAsDate && date < this.minDateAsDate) {
				return false;
			}
			if (this.maxDateAsDate && date > this.maxDateAsDate) {
				return false;
			}
			return true;
		},

		toUnixTime(date) {
			return Math.ceil(date.getTime() / 1000);
		},

		fromUnixTime(date) {
			return new Date(date * 1000);
		},

		getStatus(date) {
			let status = '';
			let statusStr = '';
			let dateAsUnixTime = this.toUnixTime(date);
			if (dateAsUnixTime === this.localValue[0] || dateAsUnixTime === this.localValue[1]) {
				status += ' date-active';
			}
			if (dateAsUnixTime > this.localValue[0] && dateAsUnixTime < this.localValue[1]) {
				status += ' date-in-range';
			}
			statusStr = status + (this.isDateWithinRange(date) ? '' : ' date-disabled') + (this.isDateHighlighted(date) ? ' date-highlight' : '');
			return statusStr;
		},

		yearClick(flag) {
			this.now.setFullYear(this.now.getFullYear() + flag);
			this.now = new Date(this.now);
		},

		monthClick(flag) {
			this.now.setMonth(this.now.getMonth() + flag, 1);
			this.now = new Date(this.now);
		},

		pickDate(index) {
			this.show = false;
			this.now = new Date(this.date[index].time);
			if (this.rangeMode) {
				if (!this.rangeSelectionActive) {
					this.rangeSelectionActive = true;
					this.localValue[0] = this.localValue[1] = this.toUnixTime(this.now);
				} else {
					this.rangeSelectionActive = false;
					this.localValue[1] = this.toUnixTime(this.now);
					if (this.localValue[0] > this.localValue[1]) {
						let tmp = this.localValue[0];
						this.localValue[0] = this.localValue[1];
						this.localValue[1] = tmp;
					}
					this.$emit('update:model-value', this.localValue);
				}
			} else {
				this.localValue[0] = this.toUnixTime(this.now);
				this.$emit('update:model-value', this.localValue);
			}
			this.pickedValue = this.stringify();
		},

		parse(str) {
			if (str && this.field.format && str.length == this.field.format.length) {
				let time = new Date();
				let map = {
					YYYY: val => time.setFullYear(val),
					MMM: undefined,
					MM: val => time.setMonth(val - 1),
					M: val => time.setMonth(val - 1),
					DD: val => time.setDate(val),
					D: val => time.setDate(val)
				};
				this.field.format.replace(/Y+|M+|D+/g, (formatPart) => {
					if (typeof map[formatPart] == 'function') {
						map[formatPart](str.slice(this.field.format.indexOf(formatPart), this.field.format.indexOf(formatPart) + formatPart.length));
					} else {
						console.warn('v-datepicker: unsupported format');
					}
				});
				return isNaN(time.getTime()) ? null : time;
			} else {
				return null;
			}
		},

		stringify(time = this.fromUnixTime(this.localValue[0]), format = 'DD.MM.YYYY') {
			let year = time.getFullYear();
			let month = time.getMonth() + 1;
			let date = time.getDate();
			let monthName = this.months[time.getMonth()];
			let map = {
				YYYY: year,
				MMM: monthName,
				MM: ('0' + month).slice(-2),
				M: month,
				DD: ('0' + date).slice(-2),
				D: date
			};
			return format.replace(/Y+|M+|D+/g, function (formatPart) {
				return map[formatPart];
			});
		},

		leave(e) {
			if (!this.$el.contains(e.target)) {
				this.close();
			}
		},

		focusLeave() {
			setTimeout(() => {
				if (!document.querySelector(':focus', this.$el)) {
					this.close();
					this.$emit('blur');
				}
			}, 0);
		},

		getLimitingDate(dateProp) {
			if (dateProp === 'now') {
				let result = new Date();
				result.setHours(0, 0, 0, 0);
				return result;
			} else if (typeof dateProp == 'string' && this.field.format === 'DD.MM.YYYY' && dateProp.length) {
				let splitDate = dateProp.split('.');
				return new Date(splitDate[2], parseInt(splitDate[1]) - 1, splitDate[0]);
			} else {
				//либу dateFns можно не юзать this.fromUnixTime делает то же самое?
				//console.log('getLimitingDate result', dateFns.parse(typeof dateProp == 'number' ? dateProp * 1000 : dateProp));
				return this.fromUnixTime(typeof dateProp == 'number' ? dateProp : dateProp);
			}
		}
	}
};
</script>